import type { CSSProperties, FC, MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Box, Typography } from '@mui/material';

import { Error, InputNumber, Text, Tooltip } from 'ui';
import { theme } from 'ui/theme';
import palette from 'ui/theme/palette';
import { MAX_WEIGHT, MIN_WEIGHT, parseWeightEntry } from './section-weight-summary.util';

/** Mirrors parseWeightEntry so both variants agree on what's valid. Rejects rather than coerces. */
export const weightFieldRules = {
  validate: (data: unknown) => {
    const value = data as string | number | undefined;

    if (value === undefined || value === null || value === '') {
      return true;
    }

    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return 'must be a number.';
    }

    // The API stores weights as integers.
    if (!Number.isInteger(parsed)) {
      return 'must be a whole number.';
    }

    return (
      (parsed >= MIN_WEIGHT && parsed <= MAX_WEIGHT) || `must be between ${MIN_WEIGHT} and ${MAX_WEIGHT}.`
    );
  }
};

interface FormVariantProps {
  /** Bound to a React Hook Form field; saves with the surrounding form. */
  variant: 'form';
  form: UseFormReturn<FieldValues>;
  name: string;
  label: string;
  descriptionText?: string;
  dataCy?: string;
}

interface InlineVariantProps {
  /** Compact input that commits on blur, since each edit is its own save. */
  variant: 'inline';
  value: number | undefined;
  onCommit: (weight: number | undefined) => void;
  tooltip: string;
  ariaLabel: string;
  /** Set on rows that are themselves clickable, so typing doesn't trigger the row. */
  stopClickPropagation?: boolean;
  dataCy?: string;
}

export type WeightInputProps = FormVariantProps | InlineVariantProps;

const INLINE_INPUT_STYLE: CSSProperties = {
  width: '64px',
  padding: '4px 6px',
  borderRadius: '4px',
  fontFamily: 'inherit',
  fontSize: '14px'
};

const InlineWeightInput: FC<InlineVariantProps> = (props) => {
  // Held locally so typing is smooth; committed on blur, since each commit is a save.
  const [draft, setDraft] = useState(props.value?.toString() ?? '');
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setDraft(props.value?.toString() ?? '');
    setIsInvalid(false);
  }, [props.value]);

  const commit = () => {
    const { isValid, weight } = parseWeightEntry(draft);

    setIsInvalid(!isValid);

    // Keep the typed value on screen and skip the save until it's valid.
    if (!isValid || weight === props.value) {
      return;
    }

    props.onCommit(weight);
  };

  return (
    <Tooltip placement="bottom" title={props.tooltip}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Text inline content="Weight" type="body3" />
        <NumericFormat
          value={draft}
          onValueChange={(values) => {
            setDraft(values.value);
            setIsInvalid(false);
          }}
          onBlur={commit}
          decimalScale={0}
          allowNegative={false}
          inputMode="numeric"
          onClick={
            props.stopClickPropagation
              ? (event: MouseEvent<HTMLInputElement>) => event.stopPropagation()
              : undefined
          }
          aria-label={props.ariaLabel}
          aria-invalid={isInvalid}
          data-cy={props.dataCy}
          data-invalid={isInvalid}
          style={{
            ...INLINE_INPUT_STYLE,
            border: `1px solid ${isInvalid ? palette.error.dark : palette.divider}`
          }}
        />
        <Text inline content="%" type="body3" />
      </Box>
    </Tooltip>
  );
};

export const WeightInput: FC<WeightInputProps> = (props) => {
  if (props.variant === 'form') {
    const validationError = props.form.formState.errors[props.name];

    return (
      <>
        {/* maxInteger is unused: it rejects >= its value, which would block a legitimate 100. onError
            returns nothing to suppress InputNumber's own "numbers only" message on an emptied field. */}
        <InputNumber
          control={props.form.control}
          name={props.name}
          label={props.label}
          rules={weightFieldRules}
          dataCy={props.dataCy}
          isIntegerOnly
          allowNegative={false}
          onError={() => undefined}
        />
        {validationError && (
          <Error
            content={`* ${props.label} ${(validationError.message as string) ?? ''}`}
            dataCy="validation-message"
          />
        )}
        {props.descriptionText && (
          <Typography
            data-cy="description-text"
            variant="body1"
            color={theme.palette.text.primary}
            fontSize="12px"
          >
            {props.descriptionText}
          </Typography>
        )}
      </>
    );
  }

  return <InlineWeightInput {...props} />;
};
